import React, { useEffect, useState, memo } from 'react';
import { setActivityStatus } from '../../services/pkiApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v2.schmalgsicht.de';

export const DrawingCanvas3D = memo(function DrawingCanvas3D({ profileId, onClose, onStatusChange }) {
  const gridCols = 80;
  const gridRows = 40;
  const [pixels, setPixels] = useState(Array(gridRows).fill(null).map(() => Array(gridCols).fill(false)));
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentColor, setCurrentColor] = useState('#3b82f6'); // Default blue

  // Responsive canvas size with resize handler
  const getCanvasWidth = () => window.innerWidth < 640 ? 320 : window.innerWidth < 1024 ? 480 : 640;
  const [canvasWidth, setCanvasWidth] = useState(getCanvasWidth);
  const canvasHeight = (canvasWidth / gridCols) * gridRows;
  const pixelSize = canvasWidth / gridCols;

  // Set initial status and handle resize
  useEffect(() => {
    onStatusChange('Werde kreativ und male Chris ein Bild');

    const handleResize = () => setCanvasWidth(getCanvasWidth());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onStatusChange]);

  const colors = [
    { name: 'Rot', value: '#ef4444' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Gelb', value: '#eab308' },
    { name: 'Grün', value: '#22c55e' },
    { name: 'Blau', value: '#3b82f6' },
    { name: 'Lila', value: '#a855f7' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Schwarz', value: '#000000' }
  ];

  const handlePixelClick = (row, col) => {
    const newPixels = pixels.map(r => [...r]);
    newPixels[row][col] = currentColor;
    setPixels(newPixels);
  };

  const handleMouseDown = (row, col) => {
    setIsDrawing(true);
    handlePixelClick(row, col);
  };

  const handleMouseEnter = (row, col) => {
    if (isDrawing) {
      handlePixelClick(row, col);
    }
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleClear = () => {
    setPixels(Array(gridRows).fill(null).map(() => Array(gridCols).fill(false)));
  };

  const handleSubmit = async () => {
    const drawnPixels = [];
    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        if (pixels[row][col]) {
          drawnPixels.push({
            row,
            col,
            color: pixels[row][col]
          });
        }
      }
    }

    if (drawnPixels.length === 0) {
      onStatusChange('Canvas ist leer');
      return;
    }

    if (drawnPixels.length < 50) {
      onStatusChange(`Zu wenig gezeichnet (${drawnPixels.length}/50 Pixel)`);
      setTimeout(() => {
        onStatusChange('Zeichne kreativ');
      }, 3000);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/profile/${profileId}/drawing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pixels: drawnPixels,
          metadata: {
            gridCols,
            gridRows,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      await response.json();

      // Set activity status for external API
      await setActivityStatus(profileId, 'drawing');

      onClose();

    } catch (error) {
      // Silent error
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 mt-8" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
      {/* Drawing Grid */}
      <div
        className="bg-white rounded-xl shadow-lg border-2 border-gray-300 overflow-hidden"
        style={{
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          display: 'grid',
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          gap: '0px'
        }}
      >
        {pixels.map((row, rowIndex) => (
          row.map((pixel, colIndex) => (
            <div
              key={`${rowIndex}-${colIndex}`}
              onMouseDown={() => handleMouseDown(rowIndex, colIndex)}
              onMouseEnter={() => handleMouseEnter(rowIndex, colIndex)}
              style={{
                width: `${pixelSize}px`,
                height: `${pixelSize}px`,
                backgroundColor: pixel || '#f9fafb',
                cursor: 'crosshair'
              }}
            />
          ))
        ))}
      </div>

      {/* Color Palette */}
      <div className="flex gap-2">
        {colors.map((color) => (
          <button
            key={color.value}
            onClick={() => setCurrentColor(color.value)}
            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl transition-all duration-200 border-2 ${
              currentColor === color.value
                ? 'ring-2 ring-offset-2 ring-gray-400 scale-110 border-white'
                : 'border-gray-300 hover:scale-105'
            }`}
            style={{ backgroundColor: color.value }}
            title={color.name}
          />
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleClear}
          className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition-all duration-300"
        >
          Löschen
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition-all duration-300"
        >
          Abbrechen
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-pink-500 hover:from-blue-600 hover:to-pink-600 text-white font-medium transition-all duration-300"
        >
          Fertig
        </button>
      </div>
    </div>
  );
});
